<template>
  <div class="toolbar-container pt-4">
    <div class="file-info-container">
      <div class="text-h4 name">
        <v-tooltip 
          bottom
          open-delay  = "500"
        >
          <template v-slot:activator="{on, attrs}">
            <span 
              v-bind  = "attrs"
              v-on    = "on"
            >
              {{ name }}
            </span>
          </template>
          {{ name }}
        </v-tooltip>
      </div>

      <div class="text-h6 path">
        {{ folder }}
        <span v-if="showRange">,
          <span class="yellow--text">[{{ firstTimecode }} - {{ lastTimecode }}]</span>
        </span>
      </div>
    </div>

    <div class="controls-container">

      <WatchersList
        v-if      = "showWatchers"
      >
      </WatchersList>

      <v-divider
        v-if      = "showWatchers"
        vertical
        class     = "mx-3"
      >
      </v-divider>

      <!-- Reopen as Growing File -->
      <v-tooltip
        bottom
        open-delay    = "500"
        v-if          = "name && !isOpenFile && extensionSupportedInOpenMode(name)"
      >
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            v-bind    = "attrs"
            v-on      = "on"
            depressed
            color     = "primary"
            class     = "px-4 py-6 mr-2"
            @click    = "onFileSelected( {path: videoPath, type: 'growing'} )"
          >
            <v-icon>mdi-reload</v-icon>
          </v-btn>
        </template>
        <span>Reload file as Growing File</span>

      </v-tooltip>

      <!-- Link -->
      <v-tooltip 
        v-if          = "showClipLink" 
        bottom
        open-delay    = "500"
      >

        <template v-slot:activator="{ on, attrs }">
          <v-btn
            depressed
            color     = "primary"
            class     = "px-4 py-6 mr-2"
            v-bind    = "attrs"
            v-on      = "on"
            @click    = "onCopyMediaPath"
          >
            <v-icon>mdi-link</v-icon>
          </v-btn>
        </template>

        <span>Generate Clip Link</span>

      </v-tooltip>

      <!-- Unload file -->
      <v-tooltip
        bottom
        open-delay    = "500"
        v-if          = "showUnload"
      >

        <template v-slot:activator="{ on, attrs }">
          <v-btn
            depressed
            color     = "primary"
            class     = "px-4 py-6 mr-2"
            v-bind    = "attrs"
            v-on      = "on"
            @click    = "onUnload"
          >
            <v-icon>mdi-file-cancel-outline</v-icon>
          </v-btn>
        </template>

        <span>Unload File</span>

      </v-tooltip>

      <!-- Close -->
      <v-tooltip 
        v-if          = "showClose" 
        bottom
        open-delay    = "500"
      >

        <template v-slot:activator="{ on, attrs }">
          <v-btn
            depressed
            color     = "primary"
            class     = "px-4 py-6"
            v-bind    = "attrs"
            v-on      = "on"
            @click    = "onClose"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </template>

        <span>Close Clip</span>

      </v-tooltip>

      <v-divider
        v-if      = "showActionDivider"
        vertical
        class     = "mx-3"
      >
      </v-divider>

      <!-- Browse -->
      <FileSelector
        v-if      = "showBrowse"
        class     = "mr-2"
        @input    = "onFileSelected($event)"
      >
      </FileSelector>

      <!-- History -->
      <RecentFileSelector
        v-if      = "showHistory"
        @input    = "onFileSelected($event)"
      >
      </RecentFileSelector>

    </div>
  </div>
</template>

<script lang="js">

// #region Imports

/* vuex */
import { mapState } from 'vuex';

/* Telestream */
import {
  ADD_SUBTITLE_SOURCE,
  COPY_MEDIA_PATH,
  FETCH_HISTORY,
  LOAD_VIDEO,
  RESET_VIDEO_STATE, UNLOAD_VIDEO
} from '@/utils/action-types';
import FileSelector from './components/FileSelector';
import RecentFileSelector from './components/RecentFileSelector';
import WatchersList from '../panes/components/annotations/WatchersList';
import { splitPath } from './utilities/path.utils';
import { PATH_TYPE } from '@/utils/constants';
import { restoreFileBrowserOpenModeExtensions } from '@/store/utilities/persistence.utils.js';

// #endregion Imports

export default {

  // #region Properties

  name:     'FileToolbar',
  computed: {
    ...mapState([
      'annotations',
      'ui',
      'videoPath',
      'videoController',
      'video',
      'videoOpen'
    ]),
    folder() {
      return (this.videoPath ? splitPath(this.videoPath).folder : undefined);
    },
    name() {
      return (this.videoPath ? splitPath(this.videoPath).filename : undefined);
    },
    showRange() {
      return (
        ((this.video.frameCount + this.video.firstFrame) > 0) &&
        (this.video.firstTimecode && this.video.lastTimecode)
      );
    },
    showWatchers() {
      return this.ui.layout.fileToolbar.actions.watchers && this.videoPath && this.annotations.watchers.length > 0;
    },
    showClipLink() {
      return this.ui.layout.fileToolbar.actions.cliplink && this.name;
    },
    showClose() {
      return this.ui.layout.fileToolbar.actions.close && this.name;
    },
    showUnload(){
      return this.name;
    },
    showActionDivider() {
      return (
        this.name &&
        (
          (this.showClipLink || this.showClose) &&
          (this.showBrowse || this.showHistory)
        )
      );
    },
    showBrowse() {
      if (this.ui.isFileBrowserHidden) {
        return false;
      }

      return this.ui.layout.fileToolbar.actions.browse;
    },
    showHistory() {
      if (this.ui.isFileBrowserHidden) {
        return false;
      }

      return this.ui.layout.fileToolbar.actions.history;
    },
    firstTimecode() {
      return this.video.firstTimecode.split('@')[0];
    },
    lastTimecode() {
      return this.video.lastTimecode.split('@')[0];
    },
    isOpenFile() {
      return !!this.videoOpen;
    }
  },
  components: {
    FileSelector,
    RecentFileSelector,
    WatchersList
  },

  // #endregion Properties

  // #region Life-Cycle

  mounted() {
    this.$store.dispatch(FETCH_HISTORY);
  },

  // #endregion Life-Cycle

  // #region Events

  methods: {

    onClose() {
      this.$store.dispatch(RESET_VIDEO_STATE);
    },

    onUnload() {
      this.$store.dispatch(UNLOAD_VIDEO);
    },

    onCopyMediaPath() {
      this.$store.dispatch(COPY_MEDIA_PATH);
    },

    onFileSelected(selection) {
      if (selection.type === PATH_TYPE.VIDEO) {
        this.$store.dispatch(
          LOAD_VIDEO,
          {
            videoPath:     selection.path,
            audioPaths:    [],
            subtitlePaths: undefined
          }
        );
      }

      else if (selection.type === PATH_TYPE.AUDIO) {
        this.$store.dispatch(
          LOAD_VIDEO,
          {
            videoPath:     this.videoPath,
            audioPaths:    selection.path,
            subtitlePaths: this.videoController.sidecarSubtitles.map(s => s.path)
          }
        );
      }

      else if (selection.type === PATH_TYPE.SUBTITLES) {
        this.$store.dispatch(ADD_SUBTITLE_SOURCE, selection.path);
      }

      else if (selection.type === PATH_TYPE.GROWING) {
        this.$store.dispatch(
          LOAD_VIDEO,
          {
            videoPath:     selection.path,
            audioPaths:    [],
            subtitlePaths: undefined,
            open:          true
          }
        );
      }

      else if (selection.type === 'multi') {
        this.$store.dispatch(
          LOAD_VIDEO,
          {
            videoPath:     selection.videoPath,
            audioPaths:    [],
            subtitlePaths: [selection.subtitlesPath]
          }
        );
      }
    },

    extensionSupportedInOpenMode(fileName) {
      var extensions = restoreFileBrowserOpenModeExtensions();
      return extensions.some(e => fileName.endsWith(e));
    }
  }

  // #endregion Events

}
</script>

<style scoped lang="scss">
.toolbar-container {
  display:        flex;
  flex-direction: row;
  align-items:    center;

  .file-info-container {
    flex:   1;
    height: 60px;
    width:  0px;

    display:          flex;
    flex-direction:   column;
    justify-content:  center;

    .name {
      margin-bottom:  -8px;
    }

    .name, .path {
      white-space:    nowrap;
      overflow:       hidden;
      text-overflow:  ellipsis;
    }
  }

  .controls-container {
    display: flex;
    flex-direction: row;
  }
}
</style>
